import { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Card, CardContent, Typography, Button, CircularProgress,
  Alert, Chip, Divider, LinearProgress, List, ListItem, ListItemText,
  Tooltip,
} from '@mui/material';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';        // climber
import RestartAltIcon from '@mui/icons-material/RestartAlt';        // rebuilder
import ExploreIcon from '@mui/icons-material/Explore';              // explorer
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';      // specialist
import { uploadResume, getProfile, deleteResume } from '../services/api';
import { useNotification } from '../context/NotificationContext';

// Archetype config — only color + icon stay in code. Label and description
// load from the i18n catalog at render time so a language switch updates
// them without remount.
const ARCHETYPE_META = {
  climber:    { color: 'primary',   Icon: TrendingUpIcon },
  rebuilder:  { color: 'warning',   Icon: RestartAltIcon },
  explorer:   { color: 'info',      Icon: ExploreIcon },
  specialist: { color: 'secondary', Icon: AutoAwesomeIcon },
};

export default function Resume() {
  const { t } = useTranslation();
  const { warning, error: notifyError } = useNotification();
  const [profile, setProfile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [loadingProfile, setLoadingProfile] = useState(true);  // start true → mount auto-load
  const [removing, setRemoving] = useState(false);
  const [message, setMessage] = useState(null); // { type: 'success'|'error', text: '' }
  const [dragging, setDragging] = useState(false);

  const loadProfile = async () => {
    setLoadingProfile(true);
    try {
      const res = await getProfile();
      setProfile(res.data);
    } catch {
      setProfile(null);
    }
    setLoadingProfile(false);
  };

  // Auto-load on mount — if user already uploaded a resume, show their
  // profile right away. Removes the explicit "Load Existing Profile" button
  // that confused users (and existed only as a fallback for the silent
  // failure path before friendly errors landed).
  useEffect(() => {
    loadProfile();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleRemove = async () => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(t('resume.remove_confirm'))) return;
    setRemoving(true);
    try {
      await deleteResume();
      setProfile(null);
      setMessage(null);
      warning(t('resume.removed_warning'));
    } catch (e) {
      notifyError(e.friendlyMessage || t('resume.remove_failed'));
    }
    setRemoving(false);
  };

  const handleFile = async (file) => {
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['pdf', 'docx'].includes(ext)) {
      setMessage({ type: 'error', text: t('resume.format_invalid') });
      return;
    }
    setUploading(true);
    setMessage(null);
    try {
      await uploadResume(file);
      setMessage({ type: 'success', text: t('resume.upload_success', { name: file.name }) });
      await loadProfile();
    } catch (e) {
      setMessage({ type: 'error', text: t('resume.upload_failed') });
    }
    setUploading(false);
  };

  const handleInputChange = (e) => {
    const file = e.target.files?.[0];
    if (file) handleFile(file);
  };

  const handleDrop = useCallback((e) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) handleFile(file);
  }, []);

  const handleDragOver = (e) => { e.preventDefault(); setDragging(true); };
  const handleDragLeave = () => setDragging(false);

  return (
    <Box p={3} maxWidth={800} mx="auto">
      {/* Page title moved to the top app bar — see Navbar.TITLE_BY_PATH */}
      <Typography variant="body2" color="text.secondary" mb={3}>
        {t('resume.intro')}
      </Typography>

      {/* Upload Area */}
      <Card
        sx={{
          borderRadius: 3, mb: 3, border: '2px dashed',
          borderColor: dragging ? 'primary.main' : 'divider',
          bgcolor: dragging ? 'primary.50' : 'background.paper',
          transition: '0.2s',
          cursor: 'pointer',
        }}
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onClick={() => document.getElementById('resume-input').click()}
      >
        <CardContent sx={{ textAlign: 'center', py: 5 }}>
          {uploading ? (
            <>
              <CircularProgress sx={{ mb: 2 }} />
              <Typography color="text.secondary">{t('resume.uploading')}</Typography>
            </>
          ) : (
            <>
              <UploadFileIcon sx={{ fontSize: 48, color: 'primary.main', mb: 1 }} />
              <Typography variant="h6" gutterBottom>{t('resume.drop_here')}</Typography>
              <Typography variant="body2" color="text.secondary">{t('resume.or_click')}</Typography>
              <Typography variant="caption" color="text.disabled" display="block" mt={1}>
                {t('resume.format_hint')}
              </Typography>
            </>
          )}
        </CardContent>
      </Card>

      <input
        id="resume-input"
        type="file"
        accept=".pdf,.docx"
        style={{ display: 'none' }}
        onChange={handleInputChange}
      />

      {message && (
        <Alert severity={message.type} sx={{ mb: 3, borderRadius: 2 }}>
          {message.text}
        </Alert>
      )}

      {/* Inline loading state during auto-load on mount. Only renders when
          profile hasn't been resolved yet AND there's no error message. */}
      {!profile && loadingProfile && !uploading && !message && (
        <Box display="flex" alignItems="center" gap={1.5} mb={3} px={1}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {t('resume.loading_profile')}
          </Typography>
        </Box>
      )}

      {/* Profile Display */}
      {profile && (
        <Card sx={{ borderRadius: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" gap={1} mb={2}>
              <CheckCircleIcon color="success" />
              <Typography variant="h6" fontWeight="bold">{t('resume.parsed_profile')}</Typography>
            </Box>

            {profile.name && (
              <Typography variant="h5" fontWeight="bold" gutterBottom>{profile.name}</Typography>
            )}
            {profile.email && (
              <Typography variant="body2" color="text.secondary">{profile.email}</Typography>
            )}
            {profile.title && (
              <Typography variant="body2" color="primary" fontWeight="bold" mb={1}>{profile.title}</Typography>
            )}

            {/* Career archetype (detected by AI). Label/description load
                from i18n so they translate when the user toggles language. */}
            {profile.archetype && ARCHETYPE_META[profile.archetype] && (() => {
              const meta = ARCHETYPE_META[profile.archetype];
              const { Icon, color } = meta;
              const label = t(`resume.archetype_${profile.archetype}`);
              const description = t(`resume.archetype_${profile.archetype}_desc`);
              const tooltip = (
                <Box sx={{ maxWidth: 280 }}>
                  <Typography variant="caption" display="block" fontWeight={600} mb={0.5}>
                    {label}
                  </Typography>
                  <Typography variant="caption" display="block" mb={profile.archetype_reasoning ? 0.75 : 0}>
                    {description}
                  </Typography>
                  {profile.archetype_reasoning && (
                    <Typography variant="caption" display="block" fontStyle="italic">
                      {t('resume.archetype_why', { reason: profile.archetype_reasoning })}
                    </Typography>
                  )}
                </Box>
              );
              return (
                <Box mb={2}>
                  <Tooltip title={tooltip} arrow placement="right">
                    <Chip
                      icon={<Icon />}
                      label={t('resume.archetype_label', { name: label })}
                      size="small"
                      color={color}
                      variant="outlined"
                    />
                  </Tooltip>
                </Box>
              );
            })()}

            <Divider sx={{ my: 2 }} />

            {/* Skills */}
            {profile.skills?.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>🛠 {t('resume.skills_section')}</Typography>
                <Box display="flex" flexWrap="wrap" gap={0.5}>
                  {profile.skills.map(skill => (
                    <Chip key={skill} label={skill} size="small" variant="outlined" color="primary" />
                  ))}
                </Box>
              </Box>
            )}

            {/* Experience */}
            {profile.experience?.length > 0 && (
              <Box mb={2}>
                <Typography variant="subtitle2" fontWeight={600} gutterBottom>{t('resume.experience_section')}</Typography>
                <List dense disablePadding>
                  {profile.experience.slice(0, 5).map((exp, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemText
                        primary={typeof exp === 'string' ? exp : `${exp.title} @ ${exp.company}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                        secondary={typeof exp === 'object' ? exp.duration : null}
                        secondaryTypographyProps={{ variant: 'caption' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Education */}
            {profile.education?.length > 0 && (
              <Box>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>🎓 {t('resume.education_section')}</Typography>
                <List dense disablePadding>
                  {profile.education.map((edu, i) => (
                    <ListItem key={i} disableGutters>
                      <ListItemText
                        primary={typeof edu === 'string' ? edu : `${edu.degree} — ${edu.institution}`}
                        primaryTypographyProps={{ variant: 'body2' }}
                      />
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {/* Match score indicator */}
            {profile.years_experience && (
              <>
                <Divider sx={{ my: 2 }} />
                <Box display="flex" alignItems="center" gap={2}>
                  <Box flex={1}>
                    <Typography variant="caption" color="text.secondary">
                      {t('resume.experience_level', { years: profile.years_experience })}
                    </Typography>
                    <LinearProgress
                      variant="determinate"
                      value={Math.min((profile.years_experience / 15) * 100, 100)}
                      sx={{ height: 6, borderRadius: 2, mt: 0.5 }}
                    />
                  </Box>
                </Box>
              </>
            )}

            {/* Remove resume — destructive action, kept at bottom of the
                card with neutral styling so it doesn't dominate. Triggers
                a confirm dialog before hitting the backend. */}
            <Divider sx={{ my: 2 }} />
            <Box display="flex" justifyContent="flex-end">
              <Button
                size="small"
                color="inherit"
                startIcon={removing ? <CircularProgress size={14} /> : <DeleteOutlineIcon fontSize="small" />}
                onClick={handleRemove}
                disabled={removing}
                sx={{ color: 'text.secondary' }}
              >
                {removing ? t('resume.removing') : t('resume.remove_button')}
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
}
